<script>
import { env } from '@shared/env';
import APIStore from '@app/services/API/Store';
import TalkizyAPI from '@app/services/API/Talkizy';
import talkizyErrorMessage from '@app/utils/talkizyErrors';
import CommunityMemberForm from '@shared/components/CommunityMemberForm.vue';
import DOMPurify from 'dompurify';
import CommunityMembersImportForm from '@shared/components/CommunityMembersImportForm.vue';
import isMeetingFinished from '@shared/utils/meeting';
import utilsMixin from '@shared/mixins/utils';

export default {
  name: 'ClasseVirtuelleMeetingApprenants',
  inject: ['meetingStore'],
  components: { CommunityMemberForm, CommunityMembersImportForm },
  mixins: [utilsMixin],
  data() {
    return {
      participants: [],
      addedEmails: [],
      isLoading: false,
      pickerOpen: false,
      importOpen: false,
      coAnimatorOpen: false,
      // Membres SSO de l'espace (chargés paresseusement à l'ouverture du picker
      // co-animateur). null = pas encore chargés.
      hostMembers: null,
      addedCoAnimatorEmails: [],
    };
  },
  computed: {
    meetingId() {
      return this.$route.params.meetingId;
    },
    storeUUID() {
      return this.$store.getters['auth/store'].uuid;
    },
    isEnded() {
      return isMeetingFinished(this.meetingStore.meeting);
    },
    talkizyMeetingURL() {
      const slug = this.meetingStore.meeting && this.meetingStore.meeting.slug;
      return slug ? `${env.talkizyURL}/meetings/${slug}` : null;
    },
    // Bilan (classe terminée) : tri par heure d'arrivée croissante ; ceux qui
    // n'ont pas rejoint (pas de joined_at) sont renvoyés en fin de liste.
    endedParticipants() {
      return [...this.participants].sort((a, b) => {
        const ta = a.joined_at ? new Date(a.joined_at).getTime() : null;
        const tb = b.joined_at ? new Date(b.joined_at).getTime() : null;
        if (ta === null && tb === null) return 0;
        if (ta === null) return 1;
        if (tb === null) return -1;
        return ta - tb;
      });
    },
    // Décompte affiché en haut à gauche du tableau : total des participants
    // avant la classe, puis uniquement les présents une fois celle-ci terminée.
    participantsCountLabel() {
      return this.isEnded
        ? 'Nombre de participants présents (dont l\'animateur)'
        : 'Nombre total de participants (dont l\'animateur)';
    },
    participantsCount() {
      if (this.isEnded) {
        return this.participants.filter((p) => p.attended).length;
      }
      return this.participants.length;
    },
    hasEmargement() {
      const m = this.meetingStore.meeting;
      return Boolean(m && m.registration_type);
    },
    canDownloadEmargement() {
      return this.isEnded && this.hasEmargement;
    },
    emargementTooltip() {
      if (!this.hasEmargement) {
        return "L'émargement n'est pas activé sur cette classe virtuelle.";
      }
      if (!this.isEnded) {
        return 'Disponible une fois la classe virtuelle terminée.';
      }
      return "Télécharger la feuille d'émargement";
    },
  },
  mounted() {
    this.fetchParticipants();
  },
  methods: {
    async fetchParticipants() {
      this.isLoading = true;
      try {
        const res = await TalkizyAPI.getMeetingParticipants(this.meetingId);
        this.participants = res.data || [];
      } catch (e) {
        this.$showMessage.error('Impossible de charger les apprenants.');
      } finally {
        this.isLoading = false;
      }
    },
    roleLabel(role) {
      const labels = {
        animator: 'Animateur principal',
        presenter: 'Co-animateur',
        participant: 'Apprenant',
      };
      return labels[role] || role;
    },
    // Vrai pour l'animateur principal et les co-animateurs (tag foncé).
    isAnimatorRole(role) {
      return role === 'animator' || role === 'presenter';
    },
    showSatisfactionComment(row) {
      if (!row.satisfaction_comment) {
        return;
      }

      this.$buefy.dialog.alert({
        title: `Commentaire de ${row.name || 'l\'apprenant'}`,
        // Le message des dialogs Buefy est rendu en HTML : le commentaire vient
        // de l'apprenant, on ne garde que le texte brut.
        message: DOMPurify.sanitize(row.satisfaction_comment, { ALLOWED_TAGS: [] }),
        confirmText: 'Fermer',
      });
    },
    fetchCustomers(url, { q }) {
      return APIStore.getCustomers(url || this.storeUUID, { q })
        .then((data) => {
          const emails = new Set([
            ...this.participants.map((p) => (p.email || '').toLowerCase()),
            ...this.addedEmails,
          ]);
          data.data = (data.data || []).filter(
            (c) => !(c.email && emails.has(c.email.toLowerCase())),
          );
          return data;
        })
        .catch((e) => {
          this.$showMessage.error('Impossible de charger les apprenants.');
          throw e;
        });
    },
    addParticipant(member) {
      return TalkizyAPI.addMeetingParticipants(this.meetingId, [{
        email: member.email,
        firstname: member.firstname,
        lastname: member.lastname,
      }]).then((res) => {
        this.addedEmails.push((member.email || '').toLowerCase());
        return res;
      }).catch((e) => {
        const message = talkizyErrorMessage(e, "L'ajout a échoué.");
        this.$showMessage.error(message);
        throw e;
      });
    },
    // Candidats co-animateur : membres SSO de l'espace ET apprenants, dans une
    // même liste (tag distinctif). On exclut ceux déjà animateur/co-animateur ou
    // déjà ajoutés. Les apprenants sont paginés ; les membres SSO (peu nombreux)
    // sont insérés en tête de la première page.
    async fetchCoAnimatorCandidates(url, { q } = {}) {
      const excluded = new Set([
        ...this.participants
          .filter((p) => p.role === 'animator' || p.role === 'presenter')
          .map((p) => (p.email || '').toLowerCase()),
        ...this.addedCoAnimatorEmails,
      ]);

      const data = await APIStore.getCustomers(url || this.storeUUID, { q })
        .catch((e) => {
          this.$showMessage.error('Impossible de charger les apprenants.');
          throw e;
        });

      data.data = (data.data || [])
        .filter((c) => c.email && !excluded.has(c.email.toLowerCase()))
        .map((c) => ({ ...c, type: 'CUSTOMER' }));

      // Membres SSO uniquement sur la première page (url absent).
      if (!url) {
        if (this.hostMembers === null) {
          try {
            const res = await TalkizyAPI.getMembers();
            this.hostMembers = (res && res.hosts) || [];
          } catch (e) {
            this.hostMembers = [];
          }
        }

        const needle = (q || '').toLowerCase();
        const members = this.hostMembers
          .filter((h) => h.email && !excluded.has(h.email.toLowerCase()))
          .filter((h) => !needle
            || (h.name || '').toLowerCase().includes(needle)
            || (h.email || '').toLowerCase().includes(needle))
          .map((h) => {
            const [firstname, ...rest] = (h.name || '').split(' ');
            return {
              uuid: `host-${h.id}`,
              email: h.email,
              firstname: firstname || h.name || h.email,
              lastname: rest.join(' '),
              type: 'USER',
            };
          });

        data.data = [...members, ...data.data];
      }

      return data;
    },
    addCoAnimator(member) {
      return TalkizyAPI.addMeetingParticipants(this.meetingId, [{
        email: member.email,
        firstname: member.firstname,
        lastname: member.lastname,
      }], 'co_animator').then((res) => {
        this.addedCoAnimatorEmails.push((member.email || '').toLowerCase());
        return res;
      }).catch((e) => {
        const message = talkizyErrorMessage(e, "L'ajout a échoué.");
        this.$showMessage.error(message);
        throw e;
      });
    },
    onCoAnimatorAdded() {
      this.fetchParticipants();
    },
    async collectParticipants(trainings) {
      const seen = new Set();
      const participants = [];
      let url = null;

      do {
        // eslint-disable-next-line no-await-in-loop
        const data = await APIStore.getCustomers(url || this.storeUUID, {
          training_uuids: trainings,
          per_page: 100,
        });
        (data.data || []).forEach((c) => {
          if (c.email && !seen.has(c.email)) {
            seen.add(c.email);
            participants.push({ email: c.email, firstname: c.firstname, lastname: c.lastname });
          }
        });
        url = data.links && data.links.next ? data.links.next : null;
      } while (url);

      return participants;
    },
    async importParticipants({ trainings }) {
      const collected = await this.collectParticipants(trainings);
      if (collected.length === 0) {
        this.$buefy.toast.open({ message: 'Aucun apprenant dans ces formations.', type: 'is-warning' });
        throw new Error('empty');
      }

      // Exclut les apprenants déjà invités (participants actuels + ceux ajoutés
      // pendant cette session) pour n'inviter que les nouveaux.
      const invitedEmails = new Set([
        ...this.participants.map((p) => (p.email || '').toLowerCase()),
        ...this.addedEmails,
      ]);
      const toInvite = collected.filter((p) => !invitedEmails.has((p.email || '').toLowerCase()));
      const alreadyInvitedCount = collected.length - toInvite.length;

      // Tous les apprenants inscrits sont déjà invités : rien à ajouter.
      if (toInvite.length === 0) {
        this.$buefy.dialog.alert({
          title: 'Apprenants déjà invités',
          message: `Les <strong>${collected.length} apprenant(s)</strong> actuellement inscrits à ces formations `
            + 'sont <strong>déjà invités</strong> à cette classe virtuelle. Aucun nouvel apprenant à ajouter.',
          type: 'is-info',
          hasIcon: true,
          icon: 'info-circle',
          iconPack: 'fas',
          confirmText: 'J\'ai compris',
        });
        return;
      }

      try {
        await TalkizyAPI.addMeetingParticipants(this.meetingId, toInvite);
        this.addedEmails.push(...toInvite.map((p) => (p.email || '').toLowerCase()));

        const alreadyLine = alreadyInvitedCount > 0
          ? `<br><br>${alreadyInvitedCount} apprenant(s) étaient <strong>déjà invité(s)</strong> et n'ont pas été ajoutés une seconde fois.`
          : '';

        this.$buefy.dialog.alert({
          title: 'Apprenants invités',
          message: `<strong>${toInvite.length} apprenant(s)</strong> ont bien été invité(s) à la classe virtuelle.${alreadyLine}`
            + '<br><br>'
            + 'Attention : seuls les apprenants <strong>actuellement inscrits</strong> à ces formations ont été '
            + 'invités. Les apprenants qui s\'inscriront <strong>par la suite</strong> ne seront <strong>pas</strong> '
            + 'invités automatiquement — vous pourrez relancer un import pour les ajouter.',
          type: 'is-success',
          hasIcon: true,
          icon: 'check-circle',
          iconPack: 'fas',
          confirmText: 'J\'ai compris',
        });
      } catch (e) {
        const message = talkizyErrorMessage(e, "L'import a échoué.");
        this.$showMessage.error(message);
        throw e;
      }
    },
    async downloadEmargement() {
      const loader = this.$buefy.loading.open();
      try {
        const res = await TalkizyAPI.exportMeetingRegistrations(this.meetingId);
        const url = URL.createObjectURL(res.data);
        const link = document.createElement('a');
        const disposition = (res.headers && res.headers['content-disposition']) || '';
        const match = disposition.match(/filename="?([^";]+)"?/);
        link.href = url;
        link.download = match ? match[1] : 'emargements.pdf';
        document.body.appendChild(link);
        link.click();
        link.remove();
        URL.revokeObjectURL(url);
      } catch (e) {
        this.$showMessage.error("Impossible de télécharger la feuille d'émargement.");
      } finally {
        loader.close();
      }
    },
    onPickerAdded() {
      this.fetchParticipants();
    },
    onImportDone() {
      this.importOpen = false;
      this.fetchParticipants();
    },
  },
};
</script>

<template>
  <div>
    <header class="is-flex is-align-items-center is-justify-content-flex-end mb-4">
      <b-tooltip :label="emargementTooltip" position="is-bottom">
        <b-button
          icon-left="download"
          :disabled="!canDownloadEmargement"
          @click="downloadEmargement"
        >
          Télécharger la feuille d'émargement
        </b-button>
      </b-tooltip>
      <b-button v-if="!isEnded" class="ml-3" type="is-primary" icon-left="user-plus" @click="pickerOpen = true">
        Ajouter un apprenant
      </b-button>
      <b-dropdown v-if="!isEnded" class="ml-3" position="is-bottom-left" aria-role="menu">
        <template #trigger>
          <b-button icon-left="plus">
            Plus d'actions
          </b-button>
        </template>
        <b-dropdown-item @click="importOpen = true">
          Importer des apprenants
        </b-dropdown-item>
        <b-dropdown-item @click="coAnimatorOpen = true">
          Ajouter un co-animateur
        </b-dropdown-item>
      </b-dropdown>
    </header>

    <p v-if="meetingStore.meeting" class="cvmapp_count mb-4">
      {{ participantsCountLabel }} : <strong>{{ participantsCount }}</strong>
    </p>

    <!-- Le tableau affiché dépend de l'état de la visio (à venir / terminée) :
         on attend aussi le meeting du store, sinon au premier rendu (refresh)
         le tableau « à venir » s'affiche par défaut puis Vue patche le b-table
         sans ré-enregistrer ses colonnes. -->
    <b-skeleton v-if="isLoading || !meetingStore.meeting" height="120" :count="2" :animated="true" />

    <div
      v-else-if="participants.length === 0"
      class="box has-text-centered has-text-grey"
    >
      Aucun apprenant pour cette visio.
    </div>

    <!-- Tableau A : visio à venir -->
    <div v-else-if="!isEnded" key="cvmapp-upcoming" class="box is-relative mxw-full">
      <b-table class="is-middle" :data="participants">
        <b-table-column
          v-slot="props"
          cell-class="cvmapp_firstname py-5"
          field="name"
          label="Apprenant"
          sortable
        >
          <router-link
            v-if="props.row.customer_uuid"
            class="cvmapp_customer-link is-block mb-3"
            :to="{ name: 'customer', params: { uuid: props.row.customer_uuid } }"
          >
            {{ props.row.name }}
            <br>
            <strong>{{ props.row.email }}</strong>
          </router-link>
          <div v-else class="is-block mb-3">
            <span>{{ props.row.name }}</span>
            <template v-if="props.row.email">
              <br>
              <strong>{{ props.row.email }}</strong>
            </template>
            <b-tooltip
              v-if="props.row.is_external"
              class="is-block mt-1"
              label="Ce participant ne fait pas partie de vos apprenants. Retrouvez plus de détails sur Talkizy."
              position="is-right"
              multilined
            >
              <span class="cvmapp_external is-size-8 is-inline-flex is-align-items-center">
                <b-icon icon="info-circle" size="is-small" class="mr-1" />
                Participant externe
              </span>
            </b-tooltip>
          </div>
        </b-table-column>

        <b-table-column v-slot="props" cell-class="is-size-8" label="Rôle" centered>
          <span
            class="tag valign-middle"
            :class="{
          'is-dark': props.row.role === 'animator',
          'is-info': props.row.role === 'presenter',
          'is-primary': !isAnimatorRole(props.row.role),
        }"
            v-text="roleLabel(props.row.role)"
          />
        </b-table-column>

        <b-table-column v-slot="props" cell-class="is-size-8" label="Date d'invitation" numeric>
          <b-tooltip>
            <template #content>
              {{ props.row.added_at | momentFromUTC | moment('DD/MM/YY [à] HH:mm') }}
            </template>
            {{ props.row.added_at | momentFromUTC | moment('from') }}
          </b-tooltip>
        </b-table-column>
      </b-table>
    </div>

    <!-- Tableau B : visio passée (bilan) -->
    <div v-else key="cvmapp-ended" class="box is-relative mxw-full">
      <b-table
        class="is-middle"
        :data="endedParticipants"
        :row-class="(row) => (row.attended ? '' : 'has-text-grey-dark cvmapp_absent')"
      >
        <b-table-column
          v-slot="props"
          cell-class="cvmapp_firstname py-5"
          field="name"
          label="Apprenant"
          sortable
        >
          <router-link
            v-if="props.row.customer_uuid"
            class="cvmapp_customer-link is-block mb-3"
            :to="{ name: 'customer', params: { uuid: props.row.customer_uuid } }"
          >
            {{ props.row.name }}
            <br>
            <strong>{{ props.row.email }}</strong>
          </router-link>
          <div v-else class="is-block mb-3">
            <span>{{ props.row.name }}</span>
            <template v-if="props.row.email">
              <br>
              <strong>{{ props.row.email }}</strong>
            </template>
            <b-tooltip
              v-if="props.row.is_external"
              class="is-block mt-1"
              label="Ce participant ne fait pas partie de vos apprenants. Retrouvez plus de détails sur Talkizy."
              position="is-right"
              multilined
            >
              <span class="cvmapp_external is-size-8 is-inline-flex is-align-items-center">
                <b-icon icon="info-circle" size="is-small" class="mr-1" />
                Participant externe
              </span>
            </b-tooltip>
          </div>
        </b-table-column>

        <b-table-column v-slot="props" cell-class="is-size-8" label="Présence" numeric>
          <b-tag :type="props.row.attended ? 'is-success' : 'is-light'">
            {{ props.row.attended ? 'Présent' : 'Absent' }}
          </b-tag>
        </b-table-column>

        <b-table-column v-slot="props" cell-class="is-size-8" label="Temps de présence" numeric>
          <span v-if="props.row.duration_seconds != null">
            {{ props.row.duration_seconds | formatSeconds }}
          </span>
          <span v-else>—</span>
        </b-table-column>

        <b-table-column v-slot="props" cell-class="is-size-8" label="Heure d'arrivée" numeric>
          <span v-if="props.row.joined_at">
            {{ props.row.joined_at | momentFromUTC | moment('HH:mm') }}
          </span>
          <span v-else>—</span>
        </b-table-column>

        <b-table-column v-slot="props" cell-class="is-size-8" label="Temps de parole" numeric>
          <span v-if="props.row.speech_time_seconds != null">
            {{ props.row.speech_time_seconds | formatSeconds }}
          </span>
          <span v-else>—</span>
        </b-table-column>

        <b-table-column v-slot="props" cell-class="is-size-8" label="Satisfaction" numeric>
          <b-tooltip
            v-if="props.row.satisfaction_rating"
            label="Voir le commentaire"
            :active="!!props.row.satisfaction_comment"
            position="is-left"
          >
            <span
              class="cvmapp_rating is-inline-flex is-align-items-center"
              :class="{ 'is-clickable': props.row.satisfaction_comment }"
              @click="showSatisfactionComment(props.row)"
            >
              <b-rate
                :value="props.row.satisfaction_rating * 1"
                size="is-small"
                disabled
              />
              <b-icon
                v-if="props.row.satisfaction_comment"
                icon="comment"
                size="is-small"
                class="ml-2 has-text-grey-dark"
              />
            </span>
          </b-tooltip>
          <span v-else>—</span>
        </b-table-column>
      </b-table>

    </div>

    <p v-if="talkizyMeetingURL" class="has-text-centered has-text-grey-dark is-size-8 mt-8">
      Retrouvez le détail complet des participants et des statistiques avancées
      sur la page de cette classe virtuelle dans Talkizy.
      <a :href="talkizyMeetingURL" target="_blank" rel="noopener">
        Voir plus de statistiques sur Talkizy
        <b-icon icon="external-link-alt" size="is-small" />
      </a>
    </p>

    <b-modal v-model="pickerOpen" has-modal-card :destroy-on-hide="true" class="cvmapp_wide-modal">
      <CommunityMemberForm
        :fetch-fn="fetchCustomers"
        :add-fn="addParticipant"
        :plan-lock="null"
        :labels="{ add_member: 'Ajouter un participant' }"
        @add="onPickerAdded"
        @cancel="pickerOpen = false"
      >
        <template #member-meta="{ member }">
          <p>{{ member.email }}</p>
        </template>
      </CommunityMemberForm>
    </b-modal>

    <b-modal v-model="importOpen" has-modal-card :destroy-on-hide="true">
      <CommunityMembersImportForm
        class="mw-640"
        :import-fn="importParticipants"
        :labels="{ import_members: 'Importer des participants' }"
        @done="onImportDone"
        @cancel="importOpen = false"
      />
    </b-modal>

    <b-modal v-model="coAnimatorOpen" has-modal-card :destroy-on-hide="true" class="cvmapp_wide-modal">
      <CommunityMemberForm
        :fetch-fn="fetchCoAnimatorCandidates"
        :add-fn="addCoAnimator"
        :plan-lock="null"
        :labels="{
          add_member: 'Ajouter un co-animateur',
          filter_label: 'Rechercher un membre de l\'espace ou un apprenant',
        }"
        @add="onCoAnimatorAdded"
        @cancel="coAnimatorOpen = false"
      >
        <template #member-meta="{ member }">
          <p class="mt-2">
            <span
              class="tag"
              :class="{
                'is-dark': member.type === 'USER',
                'is-primary': member.type === 'CUSTOMER',
              }"
            >
              {{ member.type === 'USER' ? 'Membre de l\'espace' : 'Apprenant' }}
            </span>
            <span class="ml-2">{{ member.email }}</span>
          </p>
        </template>
      </CommunityMemberForm>
    </b-modal>
  </div>
</template>

<style lang="scss" scoped>
::v-deep .cvmapp_firstname {
  max-width: 200px;
  word-wrap: break-word !important;
  word-break: break-word !important;
}

// Apprenant absent (bilan d'une classe passée) : texte barré, en plus du gris.
::v-deep tr.cvmapp_absent td {
  text-decoration: line-through;
}

.cvmapp_count {
  color: $grey-dark;
}

.cvmapp_rating {
  &.is-clickable {
    cursor: pointer;

    // Buefy force `cursor: initial` sur les étoiles d'un b-rate désactivé,
    // ce qui masque le fait que le clic (qui remonte au wrapper) ouvre le
    // commentaire : on rétablit le pointeur sur les étoiles.
    ::v-deep .rate.is-disabled .rate-item {
      cursor: pointer;
    }
  }

  ::v-deep .rate {
    margin-bottom: 0;
  }
}

.cvmapp_customer-link {
  color: $black;

  &:hover {
    color: $theme_color_primary_hover;
  }
}

// Mention « Participant externe » : même ambre que le tag is-warning is-light,
// mais en texte simple (icône + libellé), sans fond.
.cvmapp_external {
  color: #947600;

  .icon {
    vertical-align: middle;
  }
}

@include mobile {
  ::v-deep .cvmapp_firstname {
    max-width: none;
  }
}

// Élargit uniquement la modale d'ajout de participants (composant CommunityMemberForm
// partagé) sur la classe virtuelle, sans impacter ses autres usages (communauté).
// On donne une largeur réelle au wrapper .animation-content (sinon .modal-card, en
// width:100%, se réduit au contenu), et on lève le plafond 640px de .modal-card.
::v-deep .cvmapp_wide-modal {
  .animation-content {
    width: 768px;
    max-width: 100%;
  }

  .modal-card {
    max-width: 768px !important;
  }
}
</style>
